import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewEncapsulation } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { CartService } from '../../../core/services/cart.service';
import { PlatformService } from '../../../core/services/ssr/platform.service';

import { Helpers } from '../../../core/helpers';

import { ViewOrderButtonComponentAnimations } from './view-order-button.animations';

const MOV_ANIMATION_MILLISECONDS = 350;

@Component({
  selector: 'app-view-order-button',
  templateUrl: './view-order-button.component.html',
  styleUrls: ['./view-order-button.component.scss'],
  animations: [...ViewOrderButtonComponentAnimations],
  encapsulation: ViewEncapsulation.None,
})
export class ViewOrderButtonComponent implements OnInit, OnDestroy {
  @Input()
  public activeVendorId: number;

  @Output()
  public resizeContent = new EventEmitter<void>();

  public vendorName: string;
  public minimumOrderValue = 0;
  public amountRemainingToMinimumOrderValue = 0;
  public cartItemsCount = 0;
  public cartItemsPrice = 0;
  public cartPriceBeforeDiscount = 0;

  private vendorItemsTotalPrice = 0;
  private unsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    private cartService: CartService,
    private platformService: PlatformService,
    private translateService: TranslateService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.cartService.cart$.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      this.updateProperties();

      // Tell Angular to update the view since we're
      // using ChangeDetectionStrategy.OnPush
      this.changeDetectorRef.markForCheck();
    });
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  private updateProperties(): void {
    const { itemsCount, itemsTotalPrice, itemsTotalPriceBeforeDiscount } = this.cartService.getOrderInProgressInfo();
    this.cartItemsCount = itemsCount;
    this.cartItemsPrice = itemsTotalPrice;
    this.cartPriceBeforeDiscount = itemsTotalPriceBeforeDiscount;

    this.vendorName =
      this.cartService.getVendorsCount() === 1
        ? this.cartService.getFirstVendorName()
        : (this.translateService.instant('VIEW_ORDER') as string);

    this.minimumOrderValue = this.cartService.getVendorMinimumOrderValue(this.activeVendorId);
    this.vendorItemsTotalPrice = this.cartService.getVendorItemsTotalPrice(this.activeVendorId);

    const previousAmountRemaining = this.amountRemainingToMinimumOrderValue;

    this.amountRemainingToMinimumOrderValue = this.minimumOrderValue
      ? Helpers.subtractWithPrecision(this.minimumOrderValue, this.vendorItemsTotalPrice)
      : 0;

    if (
      (previousAmountRemaining <= 0 && this.amountRemainingToMinimumOrderValue > 0) ||
      (previousAmountRemaining > 0 && this.amountRemainingToMinimumOrderValue <= 0)
    ) {
      // Wait for the MOV animation to complete
      // before rasing an event for content resize
      void this.platformService.wait(MOV_ANIMATION_MILLISECONDS).then(() => {
        this.resizeContent.emit();
      });
    }
  }
}
